export const name = "DATETIME_BUCKET";
export const summary = `Gets the lower bound of the datetime bucket that contains a datetime.`;
export const reference = `
## DATETIME_BUCKET

Gets the lower bound of the datetime bucket that contains a datetime.

\`\`\`
DATETIME_BUCKET(datetime_in_bucket, bucket_width)
\`\`\`

### 

\`\`\`
DATETIME_BUCKET(datetime_in_bucket, bucket_width, bucket_origin_datetime)
\`\`\`


### Description

Gets the lower bound of the datetime bucket that contains a datetime.


### Definitions

- \`datetime_in_bucket\`: A \`DATETIME\` value that you can use to look up a datetime bucket.
- \`bucket_width\`: An \`INTERVAL\` value that represents the width of a datetime bucket. A single interval with date and time parts is supported.
- \`bucket_origin_datetime\`: A \`DATETIME\` value that represents a point in time. All buckets expand left and right from this point. If this argument isn't set, \`1950-01-01 00:00:00\` is used by default.


### Return type

\`DATETIME\`


### Examples

In the following example, the origin is omitted and the default origin,\`1950-01-01 00:00:00\` is used. All buckets expand in both directions from the origin, and the size of each bucket is 12 hours. The lower bound of the bucket in which \`my_datetime\` belongs is returned:



\`\`\`
WITH some_datetimes AS (
  SELECT DATETIME '1949-12-30 13:00:00' AS my_datetime UNION ALL
  SELECT DATETIME '1949-12-31 00:00:00' UNION ALL
  SELECT DATETIME '1949-12-31 13:00:00' UNION ALL
  SELECT DATETIME '1950-01-01 00:00:00' UNION ALL
  SELECT DATETIME '1950-01-01 13:00:00' UNION ALL
  SELECT DATETIME '1950-01-02 00:00:00'
)
SELECT DATETIME_BUCKET(my_datetime, INTERVAL 12 HOUR) AS bucket_lower_bound
FROM some_datetimes;

/*---------------------+
 | bucket_lower_bound |
 +---------------------+
 | 1949-12-30T12:00:00 |
 | 1949-12-31T00:00:00 |
 | 1949-12-31T12:00:00 |
 | 1950-01-01T00:00:00 |
 | 1950-01-01T12:00:00 |
 | 1950-01-02T00:00:00 |
 +---------------------*/

-- Some datetime buckets that originate from 1950-01-01 00:00:00:
-- + Bucket: ...
-- + Bucket: [1949-12-30 00:00:00, 1949-12-30 12:00:00)
-- + Bucket: [1949-12-30 12:00:00, 1950-01-01 00:00:00)
-- + Origin: [1950-01-01 00:00:00]
-- + Bucket: [1950-01-01 00:00:00, 1950-01-01 12:00:00)
-- + Bucket: [1950-01-01 12:00:00, 1950-02-00 00:00:00)
-- + Bucket: ...
\`\`\`

In the following example, the origin has been changed to \`2000-12-24 12:00:00\`, and all buckets expand in both directions from this point. The size of each bucket is seven days. The lower bound of the bucket in which \`my_datetime\`belongs is returned:



\`\`\`
WITH some_datetimes AS (
  SELECT DATETIME '2000-12-20 00:00:00' AS my_datetime UNION ALL
  SELECT DATETIME '2000-12-21 00:00:00' UNION ALL
  SELECT DATETIME '2000-12-22 00:00:00' UNION ALL
  SELECT DATETIME '2000-12-23 00:00:00' UNION ALL
  SELECT DATETIME '2000-12-24 00:00:00' UNION ALL
  SELECT DATETIME '2000-12-25 00:00:00'
)
SELECT DATETIME_BUCKET(
  my_datetime,
  INTERVAL 7 DAY,
  DATETIME '2000-12-22 12:00:00') AS bucket_lower_bound
FROM some_datetimes;

/*--------------------+
 | bucket_lower_bound |
 +--------------------+
 | 2000-12-15T12:00:00 |
 | 2000-12-15T12:00:00 |
 | 2000-12-15T12:00:00 |
 | 2000-12-22T12:00:00 |
 | 2000-12-22T12:00:00 |
 | 2000-12-22T12:00:00 |
 +--------------------*/

-- Some datetime buckets that originate from 2000-12-22 12:00:00:
-- + Bucket: ...
-- + Bucket: [2000-12-08 12:00:00, 2000-12-15 12:00:00)
-- + Bucket: [2000-12-15 12:00:00, 2000-12-22 12:00:00)
-- + Origin: [2000-12-22 12:00:00]
-- + Bucket: [2000-12-22 12:00:00, 2000-12-29 12:00:00)
-- + Bucket: [2000-12-29 12:00:00, 2000-01-05 12:00:00)
-- + Bucket: ...
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/time-series-functions
`;
